import React, { useEffect, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { Box, Button, Flex, Indicator, Loader, Text, Title } from "@mantine/core";
import useStyles from "./style";
import SignatureRequestModal from "../SignatureModal";
import { updateChangeSignature } from "../../services/signature";
import { acceptProviderRequest, declineProviderRequest, acceptProviderConnectionRequest, declineProviderConnectionRequest, getNotifications, getNotificationsCount } from "../../services/notification";
import { useSelector } from "react-redux";
import swal from "sweetalert";
import SkypointUploadStaffDocDashboard from "../SkypointStaffDetails/SkypointUploadStaffDocDashboard";
import store from "../../store";
import { setSelectedDocModal, setUploadDocModal } from "../../store/reducers/document";

const NewNotificationCardComp = ({
  notification,
  handleReadNotification,
  isLoading,
  isReadAll,
  profile,
  onClickNotification,
  onOpenSignatureModal,
  setPopoverOpened,
}) => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const notificationId = notification?._id || notification?.id;
  const isRead = !!notification?.readedUsers?.includes(profile?.user?.id);

  // Expired provider document this notification is about, once opened. The
  // shared modal owns its open flag in Redux, so mirror its closing here.
  const [providerDoc, setProviderDoc] = useState(null);
  const { uploadDocModal } = useSelector((state) => state?.document || {});
  useEffect(() => {
    if (!uploadDocModal) setProviderDoc(null);
  }, [uploadDocModal]);
  // const [signatureModalOpened, setSignatureModalOpened] = useState(false);
  const { signature } = useSelector((state) => state?.signature);

  const handleClick = async () => {
    // Marking read happens before any routing below: several branches navigate
    // away or return early, and this used to sit at the very bottom where an
    // early return would skip it entirely.
    if (!isRead && notificationId) {
      handleReadNotification(notificationId);
    }

    // An expired Skypoint provider document has no facility/staff/client page to
    // open, so it is resolved in place through the shared upload/preview modal.
    if (notification?.module === "document" && notification?.metadata?.provider) {
      const file = notification?.metadata?.file;
      if (file) {
        const doc = { ...file, provider: notification.metadata.provider };
        setProviderDoc(doc);
        store.dispatch(setSelectedDocModal(doc));
        store.dispatch(setUploadDocModal(true));
        return;
      }
    }

    if (notification?.module === "document") {
      const id =
        notification?.facility?.id ||
        notification?.staff?.id ||
        notification?.client?.id;
      if (id) {
        navigate(`/home/facility/${id}/view`, {
          state: { notificationDoc: notification },
        });
      }
    }

    if (notification?.module === "billing_authorization") {
      if (
        (!profile?.user?.role && !profile?.user?.type) ||
        !profile?.user?.permissions ||
        profile?.user?.permissions?.billing
      ) {
        navigate("/home/billing-claim-form", {
          state: {
            manage: "client",
          },
        });
      } else {
        swal({
          icon: "error",
          title: "You don't have permission to access Billing.",
          dangerMode: true,
        });
      }
    }

    if (notification?.module === "Invoice") {
      if (
        (!profile?.user?.role && !profile?.user?.type) ||
        !profile?.user?.permissions ||
        profile?.user?.permissions?.invoice
      ) {
        navigate("/home/invoices");
      } else {
        swal({
          icon: "error",
          title: "You don't have permission to access Invoice.",
          dangerMode: true,
        });
      }
    }

    if (notification && notification?.module === "document") {
      if (notification?.facility?.id) {
        navigate(`/home/facility/${notification?.facility?.id}/view`, {
          state: {
            notificationDoc: notification,
          },
        });
      } else if (notification?.staff) {
        navigate(`/home/staff/${notification?.staff?.id}/view`, {
          state: {
            notificationDoc: notification,
          },
        });
      } else if (notification?.client) {
        navigate(`/home/client`, {
          state: {
            notificationDoc: notification,
            //  client: notification?.client,
          },
        });
      }
    }
    if (notification && notification?.module === "patient") {
      if (signature === null || !signature) {
        swal({
          title: "Signature Required",
          text: "Please add a signature from setting page to continue",
          dangerMode: true,
          icon: "warning",
          buttons: {
            cancel: {
              text: "Cancel",
              visible: true,
              className: classes.cancelBtn,
            },
            confirm: {
              text: "Setup Signature",
              value: true,
              className: classes.setupBtn,
            },
          },
        }).then((value) => {
          if (value) {
            navigate("/home/settings", {
              state: { selectedTab: "my-signature" },
            });
          }
        });
      } else {
        navigate(`/home/client`, {
          state: {
            client: notification,
          },
        });
      }
    }

    if (notification && notification?.module === "signature") {
      if (notification?.userType === "staff") {
        navigate(`/home/settings`, { state: { selectedTab: "my-signature" } });
      } else {
        // setSignatureModalOpened(true);
        onOpenSignatureModal(notification);
      }
    }

    if (notification && notification?.module === "Care Plan") {
      navigate(`/home/client`, {
        state: {
          planDoc: notification,
          //  client: notification?.client,
        },
      });
    }

    if (notification && notification?.module === "Medication") {
      if (
        (!profile?.user?.role && !profile?.user?.type) ||
        !profile?.user?.permissions ||
        profile?.user?.permissions?.medication
      ) {
        navigate(`/home/medication`, {
          state: {
            medication: notification,
          },
        });
      } else {
        swal({
          icon: "error",
          title: "You don't have permission to access Medication.",
          dangerMode: true,
        });
      }
    }

    if (notification?.module === "SkaiToken") {
      navigate("/home/settings", { state: { selectedTab: "skai-credits" } });
    }
    if (notification && notification?.module === "Timesheet") {
      navigate(`/home/staff/manage-attendance/${notification?.staff?.id}`);
    }
  };

  // `metadata.provider` is set only for Skypoint provider-level documents. The
  // other three are inferred from whichever owner key the file carries — which
  // is why a provider document used to fall through and read "Facility file".
  const expiredFile = notification?.metadata?.file;
  // Prefer the notification's own provider ref (populated, so it has a name);
  // fall back to the provider stamped on the file itself.
  const providerRef = notification?.metadata?.provider || expiredFile?.provider;
  const getUserType = providerRef
    ? "provider"
    : Object.keys(expiredFile || {}).includes("staff")
    ? "staff"
    : Object.keys(expiredFile || {}).includes("client")
    ? "client"
    : "facility";

  const providerLabel =
    providerRef?.businessName ||
    providerRef?.name ||
    `${providerRef?.firstName || ""} ${providerRef?.lastName || ""}`.trim() ||
    null;

  const handleAcceptSignature = async () => {
    const payload = {
      signatureId: notification?.moduleId,
      staffId: notification?.staff?.id,
    };
    await updateChangeSignature(payload);
  };

  const [actionLoading, setActionLoading] = useState(false);
  const isContractorRequest = notification?.module === "contractor";
  const isProviderRequest = notification?.module === "provider";
  const isActionableRequest = isContractorRequest || isProviderRequest;

  const handleAcceptProvider = async (e) => {
    e.stopPropagation();
    setActionLoading(true);
    const success = await acceptProviderRequest(notification?.moduleId, notification?._id);
    setActionLoading(false);
    if (success) {
      swal({ title: "Provider request accepted", icon: "success" });
      handleReadNotification(notificationId);
      getNotifications({ page: 1, limit: 10, userId: profile?.user?.id });
      getNotificationsCount();
    }
  };

  const handleDeclineProvider = async (e) => {
    e.stopPropagation();
    const confirmed = await swal({
      title: "Decline Request?",
      text: "Are you sure you want to decline this provider's request?",
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Decline"],
    });
    if (confirmed) {
      setActionLoading(true);
      const success = await declineProviderRequest(notification?.moduleId, notification?._id);
      setActionLoading(false);
      if (success) {
        swal({ title: "Provider request declined", icon: "success" });
        handleReadNotification(notificationId);
        getNotifications({ page: 1, limit: 10, userId: profile?.user?.id });
        getNotificationsCount();
      }
    }
  };

  const handleAcceptProviderConnection = async (e) => {
    e.stopPropagation();
    setActionLoading(true);
    const success = await acceptProviderConnectionRequest(notification?._id);
    setActionLoading(false);
    if (success) {
      swal({ title: "Request accepted", icon: "success" });
      handleReadNotification(notificationId);
      getNotifications({ page: 1, limit: 10, userId: profile?.user?.id });
      getNotificationsCount();
    }
  };

  const handleDeclineProviderConnection = async (e) => {
    e.stopPropagation();
    const confirmed = await swal({
      title: "Decline Request?",
      text: "Are you sure you want to decline this provider's request?",
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Decline"],
    });
    if (confirmed) {
      setActionLoading(true);
      const success = await declineProviderConnectionRequest(notification?._id);
      setActionLoading(false);
      if (success) {
        swal({ title: "Request declined", icon: "success" });
        handleReadNotification(notificationId);
        getNotifications({ page: 1, limit: 10, userId: profile?.user?.id });
        getNotificationsCount();
      }
    }
  };

  return (
    <>
      <Box
        onClick={!isActionableRequest ? handleClick : undefined}
        key={notificationId}
        className={classes.card}
        style={isActionableRequest ? { cursor: "default" } : {}}
      >
        {isLoading?.includes(notificationId) ? (
          <Loader color="yellow" size={25} className={classes.loaderStyling} />
        ) : (
          <>
            <Box className={classes.titleIndicator}>
              {!isRead ? (
                <Indicator color={isReadAll ? "#737373" : "#F78C09"} />
              ) : (
                ""
              )}
              <Title order={3}>{notification?.title}</Title>
            </Box>
            {notification?.module === "document" && expiredFile?.name ? (
              <Text size="sm" color="#737373" fw={400} ff="Inter">
                {`${
                  getUserType.charAt(0).toUpperCase() + getUserType.slice(1)
                } file ${expiredFile.name}${
                  providerLabel ? ` (${providerLabel})` : ""
                } ${
                  moment(expiredFile?.expiry).isAfter(moment())
                    ? "expires"
                    : "has expired"
                }${
                  expiredFile?.expiry
                    ? ` on (${moment(expiredFile.expiry).format("MM/DD/YYYY")})`
                    : ""
                }`}
              </Text>
            ) : (
              <Text size="sm" color={"#737373"} fw={400} ff={"Inter"}>
                {notification?.message}
              </Text>
            )}

            {isContractorRequest && notification?.notificationType === "accepted" && (
              <Text size="xs" fw={600} ff="Inter" mt={8} color="#FFAF4A">
                Accepted
              </Text>
            )}
            {isContractorRequest && notification?.notificationType === "declined" && (
              <Text size="xs" fw={600} ff="Inter" mt={8} color="#F04438">
                Declined
              </Text>
            )}
            {isContractorRequest && notification?.notificationType !== "accepted" && notification?.notificationType !== "declined" && (
              <Flex gap={8} mt={8}>
                <Button
                  size="xs"
                  radius="md"
                  variant="filled"
                  onClick={handleAcceptProvider}
                  loading={actionLoading}
                  styles={{ root: { fontSize: 12, height: 30, fontFamily: "Inter", backgroundColor: "#FFAF4A", color: "#262626", "&:hover": { backgroundColor: "#FFAF4A", opacity: 0.8 } } }}
                >
                  Accept
                </Button>
                <Button
                  size="xs"
                  radius="md"
                  color="red"
                  variant="outline"
                  onClick={handleDeclineProvider}
                  disabled={actionLoading}
                  styles={{ root: { fontSize: 12, height: 30, fontFamily: "Inter" } }}
                >
                  Decline
                </Button>
              </Flex>
            )}

            {isProviderRequest && notification?.notificationType === "accepted" && (
              <Text size="xs" fw={600} ff="Inter" mt={8} color="#FFAF4A">
                Accepted
              </Text>
            )}
            {isProviderRequest && notification?.notificationType === "declined" && (
              <Text size="xs" fw={600} ff="Inter" mt={8} color="#F04438">
                Declined
              </Text>
            )}
            {isProviderRequest && notification?.notificationType !== "accepted" && notification?.notificationType !== "declined" && (
              <Flex gap={8} mt={8}>
                <Button
                  size="xs"
                  radius="md"
                  variant="filled"
                  onClick={handleAcceptProviderConnection}
                  loading={actionLoading}
                  styles={{ root: { fontSize: 12, height: 30, fontFamily: "Inter", backgroundColor: "#FFAF4A", color: "#262626", "&:hover": { backgroundColor: "#FFAF4A", opacity: 0.8 } } }}
                >
                  Accept
                </Button>
                <Button
                  size="xs"
                  radius="md"
                  color="red"
                  variant="outline"
                  onClick={handleDeclineProviderConnection}
                  disabled={actionLoading}
                  styles={{ root: { fontSize: 12, height: 30, fontFamily: "Inter" } }}
                >
                  Decline
                </Button>
              </Flex>
            )}

            <Text size="sm" color={"#737373"} fw={400} ff={"Inter"}>
              {moment(notification?.createdAt)?.fromNow()}
            </Text>
          </>
        )}
      </Box>

      {providerDoc && (
        <SkypointUploadStaffDocDashboard
          isPendingAction
          uploadedFiles={providerDoc}
          providerId={
            providerDoc?.provider?.id ||
            providerDoc?.provider?._id ||
            providerDoc?.provider
          }
          onResolved={() => setProviderDoc(null)}
        />
      )}
    </>
  );
};

export default NewNotificationCardComp;
